import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Divider } from '@lightspeed/flame/Divider';
import { Text } from '@lightspeed/flame/Text';
import isEqual from 'lodash/isEqual';

import { addAllAccess, restoreShops, setError, setShops } from 'pages/store/actions/employeeShops';
import { getStore } from 'pages/store/selectors/employeeShops';

import AllShopsAccessSwitch from '../components/AllShopsAccessSwitch';
import EditShopsButton from '../components/EditShopsButton';
import EditShopsModal from '../components/EditShopsModal';
import Loading from '../components/Loading';
import useEmployeeShops from '../hooks/useEmployeeShops';
import { Shop } from '../types/shops';
import { getIsValidEmployeeID } from '../utils/employee';
import {
  putEmployeeShops,
  showPutEmployeeShopsSuccess,
  trackPutEmployeeShopsError,
} from '../utils/employeeShops';
import useSaveBtn from '../utils/saveBtn';

// Handles leaving with unsaved changes
let globalEmployeeID: string = '';
let hasUnsavedChanges: boolean = false;
let ignoreUnsavedChanges: boolean = false;
let isLeavingWithoutShops: boolean = false;
let lastEmployeeShops: Shop[] = [];
let shouldAttachSaveBtnEvent: boolean = false;

interface Props {
  isPrimaryUser: boolean;
  readOnly?: boolean;
}

const EmployeeShopsModule: React.FC<Props> = ({ isPrimaryUser = false, readOnly = false }) => {
  const { fetchEmployeeShops } = useEmployeeShops({ isPrimaryUser });

  const [isEditShopOpen, setIsEditingShopOpen] = React.useState(false);
  const [isSwitchChecked, setIsSwitchChecked] = React.useState(false);
  const [isSwitchEnabled, setIsSwitchEnabled] = React.useState(false);

  const dispatch = useDispatch();
  const { canChangeFullAccess, employeeID, error, isLoading, shops } = useSelector(getStore);
  const { saveBtnToggleDirty, saveBtnDetachEvent, saveBtnAttachEvent } = useSaveBtn();

  const isValidEmployeeID = getIsValidEmployeeID(employeeID);

  const handleOnChangeSwitch = () => {
    setIsSwitchChecked((isChecked) => {
      const newIsChecked = !isChecked;
      const hasFullAccess = shops.every((shop) => shop.hasAccess);

      if (newIsChecked && !hasFullAccess) {
        shouldAttachSaveBtnEvent = true;
        dispatch(addAllAccess());
      }

      return newIsChecked;
    });
  };

  const handleEditClick = () => {
    if (error) {
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
      dispatch(setError(undefined));
    }
    setIsEditingShopOpen(true);
  };

  const handleOnCloseModal = () => {
    window.hideNotifier?.('warning', 1);
    dispatch(restoreShops()); // Closing the modal without saving restores shops to default
  };

  const handleOnSaveModal = () => saveEmployeeShops();

  const saveEmployeeShops = async () => {
    const employeeShops = shops.filter((shop) => shop.hasAccess);

    try {
      await putEmployeeShops({ employeeID, employeeShops });

      // Updates default shops
      const newShops = shops.map((shop) => ({ ...shop, isDefault: shop.hasAccess }));

      shouldAttachSaveBtnEvent = false;
      dispatch(setShops(newShops));
      saveBtnToggleDirty(false);
      setIsEditingShopOpen(false);
      showPutEmployeeShopsSuccess();
    } catch (error) {
      trackPutEmployeeShopsError({ error, dispatch });
    }
  };

  React.useEffect(() => {
    fetchEmployeeShops();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const initialShops = shops.filter((shop) => shop.isDefault);

    globalEmployeeID = employeeID;
    lastEmployeeShops = shops.filter((shop) => shop.hasAccess);
    hasUnsavedChanges = !isEqual(initialShops, lastEmployeeShops);
    isLeavingWithoutShops = !lastEmployeeShops.length;
    ignoreUnsavedChanges = isPrimaryUser || !isValidEmployeeID;

    // Handles save changes button behavior
    if (shouldAttachSaveBtnEvent) {
      saveBtnDetachEvent(saveEmployeeShops);
      saveBtnAttachEvent(saveEmployeeShops);
      saveBtnToggleDirty(true);
    }

    setIsSwitchChecked(isPrimaryUser || lastEmployeeShops.length === shops.length);
  }, [shops, isPrimaryUser]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setIsSwitchEnabled(canChangeFullAccess);
  }, [canChangeFullAccess]);

  if (isLoading) {
    return <Loading />;
  }

  // employeeID === null means new employee
  // TODO: review this
  if (!isValidEmployeeID) {
    return <Text>Save the changes to edit the location access</Text>;
  }

  return (
    <>
      <AllShopsAccessSwitch
        checked={isSwitchChecked}
        disabled={!isSwitchEnabled || readOnly}
        onChangeSwitch={handleOnChangeSwitch}
      />
      <Divider />
      <EditShopsButton disabled={isSwitchChecked} onClickEdit={handleEditClick} />
      <EditShopsModal
        isOpen={isEditShopOpen}
        onCloseModal={handleOnCloseModal}
        onSaveModal={handleOnSaveModal}
        setIsOpen={setIsEditingShopOpen}
        readOnly={readOnly}
      />
    </>
  );
};

interface GetEmployeeShopStateOutput {
  employeeID: string;
  employeeShops: Shop[];
  ignoreUnsavedChanges: boolean;
  isLeavingWithoutShops: boolean;
  hasUnsavedChanges: boolean;
}

export const getEmployeeShopState = (): GetEmployeeShopStateOutput => ({
  employeeID: globalEmployeeID,
  employeeShops: lastEmployeeShops,
  ignoreUnsavedChanges,
  isLeavingWithoutShops,
  hasUnsavedChanges,
});

export default EmployeeShopsModule;
